import axios from "axios";
import fileDownload from "js-file-download";
import axiosInstance from "../api";
import { BlockBlobClient } from "@azure/storage-blob";
import toastMessages from "./helpers/toastMessage";
import { normalizeUploadFile } from "./helpers/fileNameNormalizer";
interface PresignedUrlArgs {
 type: "image" | "document" | "images/esignatures";
 token: string;
 file: File;
}

export interface UploadToAzureResult {
 filepath: string;
 presignedUrl: string;
}

interface pdfNameAndIds {
 id: number;
 fileName: string;
}

type ObjectWithFiles = {
 [key: string]: File;
};

function parseFilenameFromContentDisposition(
 header: string | undefined
): string | null {
 if (!header) return null;

 const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
 if (utf8Match?.[1]) {
  try {
   return decodeURIComponent(utf8Match[1].trim());
  } catch {
   return utf8Match[1].trim();
  }
 }

 const quotedMatch = /filename\s*=\s*"([^"]+)"/i.exec(header);
 if (quotedMatch?.[1]) return quotedMatch[1].trim();

 const bareMatch = /filename\s*=\s*([^;]+)/i.exec(header);
 if (bareMatch?.[1]) return bareMatch[1].trim();

 return null;
}

function getTodayYmd(): string {
 const d = new Date();
 const yyyy = d.getFullYear();
 const mm = String(d.getMonth() + 1).padStart(2, "0");
 const dd = String(d.getDate()).padStart(2, "0");
 return `${yyyy}-${mm}-${dd}`;
}

async function extractBlobErrorMessage(error: any): Promise<string> {
 const fallback = "Something went wrong";
 const data = error?.response?.data;

 if (data instanceof Blob) {
  try {
   const text = await data.text();
   const parsed = JSON.parse(text);
   return parsed?.message ?? fallback;
  } catch {
   return error?.message ?? fallback;
  }
 }

 return data?.message ?? error?.message ?? fallback;
}

class FileActions {
 private _getExt(filename: string) {
  const ext = filename.split(".").pop();
  if (ext == filename) return "";
  return ext;
 }

 downloadFile({
  url,
  filename,
 }: {
  url: string;
  filename: string;
 }): Promise<void> {
  return new Promise((resolve, reject) => {
   axios
    .get(url, { responseType: "blob" })
    .then((response) => {
     resolve(fileDownload(response.data, filename));
    })
    .catch((error) => reject(error));
  });
 }

 async viewReports({
  id,
  token,
 }: {
  id: number;
  token: string;
 }): Promise<string[]> {
  return new Promise(async (resolve, reject) => {
   if (!!token === false)
    reject(new Error("Not authorized to perform the action"));

   try {
    const response = await axiosInstance.get(
     `/v1/orders/${id}/new-flow/get-report`,
     { headers: { Authorization: `Bearer ${token}` } }
    );

    if (response.status === 200 && response.data.data.reportUrl?.length > 0) {
     resolve(response?.data?.data?.reportUrl);
    } else reject(new Error("No report present to download"));
   } catch (error) {
    reject(error);
   }
  });
 }

 async downloadReport({
  id,
  token,
  fileName,
 }: {
  id: number;
  token: string;
  fileName: string;
 }) {
  return new Promise(async (resolve, reject) => {
   if (!!token === false)
    reject(new Error("Not authorized to perform the action"));

   try {
    const response = await axiosInstance.get(
     `/v1/orders/${id}/new-flow/get-report`,
     { headers: { Authorization: `Bearer ${token}` } }
    );

    if (response.status === 200 && response.data.data.reportUrl?.length > 0) {
     Promise.allSettled(
      response.data.data.reportUrl.map((url: string, index: number) =>
       this.downloadFile({
        url,
        filename: `${fileName}${index + 1} Report.pdf`,
       })
      )
     )
      .then((results) => resolve(results))
      .catch((error) => reject(error));
    } else reject(new Error("No report present to download"));
   } catch (error) {
    reject(error);
   }
  });
 }

 async downloadMultiplePDF(
  data: Array<{ id: number; fileName: string }>,
  token: string
 ) {
  try {
   const orderIds = data.map((item) => item.id);

   const response = await axiosInstance.post(
    `v1/orders/new-flow/multiple-download`,
    { orderIds },
    {
     headers: { Authorization: `Bearer ${token}` },
     responseType: "blob",
    }
   );

   const filename =
    parseFilenameFromContentDisposition(
     response.headers?.["content-disposition"]
    ) ?? `orders_${getTodayYmd()}.zip`;

   fileDownload(response.data, filename);
  } catch (error: any) {
   const message = await extractBlobErrorMessage(error);
   toastMessages.error(message);
  }
 }

 private async _getPresignedUrl({
  type,
  token,
  file,
 }: PresignedUrlArgs): Promise<{
  url: string;
  filepath: string;
  file: File;
 }> {
  return new Promise((resolve, reject) => {
   const normalizedFile = normalizeUploadFile(file);
   const url = `v1/pre-signed-url/${
    type === "image" || type === "images/esignatures" ? "images" : "documents"
   }/${normalizedFile.name.replace(/ /g, "_")}`;

   axiosInstance
    .get(url, {
     headers: { Authorization: `Bearer ${token}` },
    })
    .then((response) => {
     if (response.data.url && response.data.filepath) {
      resolve({
       url: response.data.url,
       filepath: response.data.filepath,
      file: normalizedFile,
      });
     } else reject(new Error("Could not generate URL"));
    })
    .catch((error) => reject(error));
  });
 }

 async uploadToAzureWithMetadata({
  type,
  file,
  token,
 }: PresignedUrlArgs): Promise<UploadToAzureResult> {
  return new Promise((resolve, reject) => {
   const normalizedFile = normalizeUploadFile(file);
   this._getPresignedUrl({
    type,
    token,
    file: normalizedFile,
   })
    .then((response) => {
     const blockBlobClient = new BlockBlobClient(response.url);

     blockBlobClient
      .uploadData(normalizedFile, {
       blobHTTPHeaders: { blobContentType: normalizedFile.type },
      })
      .then(() =>
       resolve({
        filepath: response.filepath,
        presignedUrl: response.url,
       })
      )
      .catch((error) => reject(error));
    })
    .catch((error) => reject(error));
  });
 }

 async uploadToAzure({ type, file, token }: PresignedUrlArgs): Promise<string> {
  const result = await this.uploadToAzureWithMetadata({ type, file, token });
  return result.filepath;
 }

 UploadFiles({
  files,
  token,
 }: {
  files: ObjectWithFiles | File[];
  token: string;
 }): Promise<Object | string[]> {
  const fileActions = new FileActions();
  const isArrayOfFiles = Array.isArray(files);
  let promises: any = [];

  if (!isArrayOfFiles) {
   promises = Object.keys(files).map((key) => {
    if (files[key] instanceof File) {
     return fileActions.uploadToAzure({
      type: "document",
      file: files[key],
      token,
     });
    }
   });
  } else {
   promises = files.map((file) =>
    fileActions.uploadToAzure({ type: "document", file, token })
   );
  }

  return new Promise((resolve, reject) => {
   Promise.allSettled(promises).then((results) => {
    const updatedFilesPayload: any = isArrayOfFiles ? [] : {};

    results.map((result, index) => {
     if (result.status === "fulfilled") {
      if (!isArrayOfFiles) {
       const fileKey = Object.keys(files).find(
        (key, fileIndex) => fileIndex === index
       );

       if (fileKey)
        updatedFilesPayload[fileKey] = !!result.value ? result.value : "";
      } else {
       updatedFilesPayload.push(!!result.value ? result.value : "");
      }
     }
    });

    resolve(updatedFilesPayload);
   });
  });
 }

 convertBase64ToImage(
  url: string,
  fileName: string
 ): Promise<File | undefined> {
  return new Promise((resolve, reject) => {
   fetch(url)
    .then((res) => res.blob())
    .then((blob) => {
     const file = new File([blob], fileName, { type: "image/png" });
     resolve(file);
    })
    .catch((error) => reject(new Error(error.message)));
  });
 }
}

export default FileActions;
